import React from "react";
import ReactDOMServer from "react-dom/server";
import { styled } from "goober";
import { axisBottom, axisLeft, axisRight, axisTop } from "d3-axis";
import type { ScaleBand } from "d3-scale";
import { select } from "d3-selection";
import type { CurveFactory } from "d3-shape";
import {
  area as d3area,
  curveLinear,
  curveNatural,
  line as d3line,
} from "d3-shape";

import CaretDown from "@/inline-assets/caret-down.svg";
import CaretUp from "@/inline-assets/caret-up.svg";

export type CircleDataParams = {
  [key: string]: number;
};

type AreaProps = {
  data: CircleDataParams[];
  xField: string;
  yField: string;
  xScale: (a: number) => number;
  yScale: (a: number) => number;
  className?: string;
  clipPathId: string;
  style?: React.CSSProperties;
};

type CircleProps<T> = {
  data: CircleDataParams[];
  xScale: (a: number) => number;
  yScale: (a: number) => number;
  radius: string | number;
  xField: string;
  yField: string;
  className?: string;
  onMouseOver: (p: {
    data: T;
    event: React.MouseEvent<SVGCircleElement>;
  }) => void;
  onMouseOut: () => void;
  clipPathId: string;
  color?: (d: { [key: string]: number }) => string;
  circleClassName?: string;
  style?: React.CSSProperties;
};

const CaretContainer = styled("foreignObject")`
  pointer-events: none;
  cursor: pointer;

  svg {
    width: 1rem;
  }
`;

const curveTypes: Record<string, CurveFactory> = {
  natural: curveNatural,
  linear: curveLinear,
};

const useD3 = (
  renderChartFn: (selection: ReturnType<typeof select>) => void,
  dependencies: React.DependencyList,
) => {
  const ref = React.useRef<SVGGElement>();

  React.useEffect(() => {
    renderChartFn(select(ref.current));
    return () => {};
  }, [renderChartFn, dependencies]);
  return ref;
};

function AreaWithGradient({
  data,
  xScale,
  yScale,
  xField,
  yField,
  className,
  clipPathId,
  curveType = "linear",
  color,
  gradientId = "areaGradient",
  opacity = 0.15,
}) {
  const area = d3area()
    .curve(curveTypes[curveType])
    .x((d) => xScale(d[xField]))
    .y1((d) => yScale(d[yField]))
    .y0(() => yScale(0));

  return (
    <g className={className} clipPath={clipPathId ? `url(#${clipPathId})` : ""}>
      <defs>
        <linearGradient id={gradientId} gradientTransform="rotate(90)">
          <stop offset="0%" stopColor={color} stopOpacity="1" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d={area(data)}
        style={{ fill: `url(#${gradientId})`, fillOpacity: opacity }}
      ></path>
    </g>
  );
}

function Area({
  data,
  xScale,
  yScale,
  xField,
  yField,
  className,
  clipPathId,
  style,
}: AreaProps) {
  const area = d3area()
    .x((d) => xScale(d[xField]))
    .y1((d) => yScale(d[yField]))
    .y0(() => yScale(0));

  return (
    <g
      className={className}
      clipPath={clipPathId ? `url(#${clipPathId})` : ""}
      style={style}
    >
      {/* @ts-expect-error */}
      <path d={area(data)}></path>
    </g>
  );
}

type SVGRectProps = React.DetailedHTMLProps<React.SVGProps<SVGRect>, SVGRect>;

interface BarProps<TData> {
  data: TData[];
  xScale?: ScaleBand<TData>;
  yScale?: ScaleBand<TData>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  xField?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  yField?: any;
  className?: string;
  clipPathId?: string;
  style?: React.CSSProperties;
  height?: number;
  color?: (d: TData) => SVGRectProps["fill"];
  margin?: { bottom: number };
  tooltipContent?: (d: TData) => React.ReactNode;
}

function Bar<TData>({
  data,
  xScale,
  yScale,
  xField,
  yField,
  className,
  clipPathId,
  style,
  height,
  color,
  margin,
  tooltipContent,
}: BarProps<TData>) {
  const avatarMargin = 12;
  return (
    <g
      className={className}
      clipPath={clipPathId ? `url(#${clipPathId})` : ""}
      style={style}
    >
      {data.map((d) => {
        const tooltip = ReactDOMServer.renderToStaticMarkup(tooltipContent(d));
        return (
          <g key={d[xField]}>
            <rect
              x={xScale(d[xField])}
              y={yScale(d[yField])}
              width={xScale.bandwidth()}
              height={height - margin.bottom - yScale(d[yField])}
              fill={color ? color(d[xField]) : "teal"}
              data-tooltip={tooltip}
            />
            <image
              // TODO: Refactor this so we aren't dependent on an "avatar" field.
              // @ts-expect-error
              xlinkHref={d.avatar}
              x={xScale(d[xField]) + xScale.bandwidth() / 2 - avatarMargin}
              y={yScale(d[yField]) - avatarMargin * 3}
              width={24}
              height={24}
            />

            {
              // TODO: Refactor this so we aren't dependent on an "avatar" field.
              // @ts-expect-error
              d.avatar ? (
                <circle
                  cx={xScale(d[xField]) + xScale.bandwidth() / 2}
                  cy={yScale(d[yField]) - avatarMargin * 2}
                  r={14}
                  strokeWidth={2}
                  stroke={color ? color(d[xField]) : "teal"}
                  fill="none"
                />
              ) : null
            }
          </g>
        );
      })}
    </g>
  );
}

function BarWithLid({
  data,
  xScale,
  yScale,
  xField,
  yField,
  className,
  clipPathId,
  style,
  color,
  tooltipContent,
  height,
  id,
  radius = 5,
}) {
  return (
    <g
      className={className}
      clipPath={clipPathId ? `url(#${clipPathId})` : ""}
      style={style}
    >
      {data.map((d, i) => {
        const tooltip = tooltipContent
          ? ReactDOMServer.renderToStaticMarkup(tooltipContent(d))
          : null;
        const clipId = `clip-${id}-${d[xField]}`;
        const gradientId = `bar-group-${i}-gradient-${id}`;
        return (
          <g key={`bar-group-${i}`} data-tooltip={tooltip}>
            <g clipPath={`url(#${clipId})`}>
              <rect
                x={xScale(d[xField])}
                y={yScale(d[yField])}
                width={xScale.bandwidth()}
                height={3}
                fill={color ? color(d[xField]) : "teal"}
              />
              <rect
                x={xScale(d[xField])}
                y={yScale(d[yField]) + 3}
                width={xScale.bandwidth()}
                height={height - yScale(d[yField])}
                fill={`url(#${gradientId})`}
              />
              <defs>
                <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
                  <stop stopColor={color(d[xField])} stopOpacity="0.15" />
                  <stop
                    offset="1"
                    stopColor={color(d[xField])}
                    stopOpacity="0.02"
                  />
                </linearGradient>
              </defs>
            </g>
            <defs>
              <clipPath id={clipId}>
                <path
                  d={`
                    M${xScale(d[xField])},${yScale(d[yField]) + radius}
                    A ${radius} ${radius} 0 0 1 ${
                      xScale(d[xField]) + radius
                    } ${yScale(d[yField])}
                    L${
                      xScale(d[xField]) + xScale.bandwidth() - radius
                    },${yScale(d[yField])}
                    A ${radius} ${radius} 0 0 1 ${
                      xScale(d[xField]) + xScale.bandwidth()
                    },${yScale(d[yField]) + radius}
                    L${xScale(d[xField]) + xScale.bandwidth()},${height}
                    L${xScale(d[xField])},${height}
                    L${xScale(d[xField])},${yScale(d[yField]) + radius}
                    Z
                  `}
                  fill="white"
                />
              </clipPath>
            </defs>
          </g>
        );
      })}
    </g>
  );
}

function Circle<T>({
  data,
  xScale,
  yScale,
  radius,
  xField,
  yField,
  className,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onMouseOver = (p: {
    data: T;
    event: React.MouseEvent<SVGCircleElement>;
  }) => {},
  onMouseOut = () => {},
  clipPathId,
  color,
  circleClassName,
  style,
}: CircleProps<T>) {
  return (
    <g className={className} clipPath={clipPathId ? `url(#${clipPathId})` : ""}>
      {data.map((d, index) => {
        return (
          <circle
            cx={xScale(d[xField])}
            cy={yScale(d[yField])}
            r={radius}
            key={index}
            onMouseOver={(event) => {
              onMouseOver({ event, data: d as T });
            }}
            onMouseOut={onMouseOut}
            className={circleClassName}
            style={color ? { fill: color(d) } : style ? style : {}}
          />
        );
      })}
    </g>
  );
}

function VerticalCrossHair({ xStart, xEnd, yStart, yEnd, className }) {
  return (
    <path
      className={className}
      d={`M ${xStart} ${yStart} L ${xEnd} ${yStart} L ${xEnd} ${yEnd} L ${xStart} ${yEnd} Z`}
    ></path>
  );
}

function HorizontalGridLines({ xScale, yScale, className, ticks }) {
  const range = xScale.range();
  return (
    <g className={className}>
      {ticks.map((tickValue, index) => {
        const yPx = yScale(tickValue);
        return (
          <path key={index} d={`M ${range[0]} ${yPx} L ${range[1]} ${yPx}`} />
        );
      })}
    </g>
  );
}

function XAxisWithImage({
  xScale,
  className,
  getImage,
  imageHeight,
  imageWidth,
  height,
  margin,
}) {
  const axis = xScale.domain().map((d) => {
    return {
      image: getImage(d),
      x: xScale(d),
      y: 0,
    };
  });

  const step = xScale.bandwidth();

  return (
    <g
      className={className}
      transform={`translate(0,${height - margin.bottom + 10})`}
    >
      {axis.map((tick, index) => {
        return (
          <g
            transform={`translate(${tick.x + step / 2}, ${tick.y})`}
            key={index}
          >
            <image
              href={tick.image}
              x={-imageWidth / 2}
              y={0}
              width={imageWidth}
              height={imageHeight}
            ></image>
          </g>
        );
      })}
    </g>
  );
}

function YAxisWithImage({
  yScale,
  offset,
  className,
  images,
  imageWidth,
  imageHeight,
  yAxisConf,
}) {
  const imageInfo = [0, 1, 2].map((d, i) => {
    return {
      image: images[i],
      x: 0,
      y: yScale(yAxisConf.tickValues[i]),
    };
  });

  return (
    <g className={className} transform={`translate(${offset}, 0)`}>
      {imageInfo.map((tick, index) => {
        return (
          <g transform={`translate(${tick.x}, ${tick.y})`} key={index}>
            <foreignObject
              x={-imageWidth / 2}
              y={-imageHeight / 2}
              width={imageWidth}
              height={imageHeight}
            >
              {tick.image}
            </foreignObject>
          </g>
        );
      })}
    </g>
  );
}

function Line({
  data,
  xScale,
  yScale,
  xField,
  yField,
  className,
  clipPathId,
  curveType = "linear",
  yOffset = 0,
  xOffset = 0,
  style = {},
}) {
  const line = d3line()
    .curve(curveTypes[curveType])
    .x((d) => xScale(d[xField]) + xOffset)
    .y((d) => yScale(d[yField]) + yOffset)
    .defined((d) => d[yField] !== null);

  return (
    <g className={className} clipPath={clipPathId ? `url(#${clipPathId})` : ""}>
      <path d={line(data)} style={{ ...style, fillOpacity: 0 }}></path>
    </g>
  );
}

function Triangle({
  data,
  xScale,
  yScale,
  xField,
  yField,
  className,
  clipPathId,
  imageSize = 16,
  dy = 10,
}) {
  return (
    <g className={className} clipPath={clipPathId ? `url(#${clipPathId})` : ""}>
      {data.map((d, index) => {
        const x = xScale(d[xField]);
        const y = yScale(d[yField]);

        if (!d.promotion) return null;

        return (
          <CaretContainer
            key={index}
            transform={`translate(${x},${d.promotion === 2 ? y - dy : y + dy})`}
            x={-imageSize / 2}
            y={-imageSize / 2}
            width={imageSize}
            height={imageSize}
            style={{
              color: d.promotion === 2 ? "var(--turq)" : "var(--red)",
            }}
          >
            {d.promotion === 2 ? <CaretUp /> : <CaretDown />}
          </CaretContainer>
        );
      })}
    </g>
  );
}

const xAxis = (
  container,
  scale,
  offset,
  { tickCount, orientation, tickFormat, tickValues },
) => {
  const axisCls = orientation === "top" ? axisTop : axisBottom;
  const axis = axisCls(scale)
    .ticks(tickCount)
    .tickSizeOuter(0)
    .tickSizeInner(0);

  if (tickFormat) axis.tickFormat(tickFormat);
  if (tickValues) axis.tickValues(tickValues);
  container
    .attr("transform", `translate(0,${offset})`)
    .call(axis)
    .call((g) => g.select(".domain").remove());
};

function XAxis({ xScale, offset, xAxisConf, className }) {
  const ref = useD3(
    (group) => {
      xAxis(group, xScale, offset, xAxisConf);
    },
    [xScale, xAxisConf.show],
  );

  return <g ref={ref} className={className} style={{ fontWeight: 600 }}></g>;
}

const yAxis = (
  container,
  scale,
  xOffset,
  yOffset,
  { tickCount, tickFormat, tickValues, orientation },
) => {
  const axisCls = orientation === "right" ? axisRight : axisLeft;
  const axis = axisCls(scale).tickSizeOuter(0).tickSizeInner(0);
  if (tickCount) axis.ticks(tickCount);
  if (tickFormat) axis.tickFormat(tickFormat);
  if (tickValues) axis.tickValues(tickValues);
  return container
    .attr("transform", `translate(${xOffset}, ${yOffset})`)
    .call(axis)
    .call((g) => g.select(".domain").remove());
};

function YAxis({ yScale, yAxisConf, xOffset, yOffset, tickValues, className }) {
  const ref = useD3(
    (group) => {
      yAxis(group, yScale, xOffset, yOffset, { ...yAxisConf, tickValues });
    },
    [yAxisConf.show, yAxisConf.tickCount, yScale],
  );
  return <g ref={ref} className={className} style={{ fontWeight: 600 }}></g>;
}

function SecondaryYAxis({
  yScale,
  y2Scale,
  xOffset,
  yOffset,
  yAxisConf,
  tickValues,
  className,
}) {
  const ref = useD3(
    (group) => {
      const newTicks = [];
      for (const tick of tickValues) {
        const y = yScale(tick);
        newTicks.push(y2Scale.invert(y));
      }
      yAxis(group, y2Scale, xOffset, yOffset, {
        ...yAxisConf,
        tickValues: newTicks,
      });
    },
    [yAxisConf.show, yAxisConf.tickCount, yScale],
  );
  return <g ref={ref} className={className} />;
}

export {
  Area,
  AreaWithGradient,
  Bar,
  BarWithLid,
  Circle,
  HorizontalGridLines,
  Line,
  SecondaryYAxis,
  Triangle,
  useD3,
  VerticalCrossHair,
  XAxis,
  XAxisWithImage,
  YAxis,
  YAxisWithImage,
};
